import React, { useState, useEffect } from 'react';
import propertyService from '../../services/propertyService';
import './AdminDashboard.css';

const ManagePropertiesTab = ({ onPropertyChanged }) => {
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const propertiesPerPage = 10;
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  
  // Delete confirmation
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch properties on component mount
  useEffect(() => {
    fetchProperties();
  }, []);

  // Update filtered properties when filters change
  useEffect(() => {
    filterProperties();
  }, [searchTerm, statusFilter, typeFilter, properties]);

  const fetchProperties = async () => {
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      console.log('ManagePropertiesTab: Fetching properties');
      // Pass a very high limit to get all properties for admin dashboard
      // Also pass page=1 to ensure we start from the beginning
      const response = await propertyService.getProperties({ limit: 10000, page: 1 });
      
      console.log('ManagePropertiesTab: Response:', response);
      
      if (response && response.data) {
        console.log(`ManagePropertiesTab: Found ${response.data.length} properties`);
        setProperties(response.data);
        setFilteredProperties(response.data);
        calculatePagination(response.data);
      } else if (response && Array.isArray(response)) {
        // Handle case where API returns array directly
        console.log(`ManagePropertiesTab: Found ${response.length} properties (array format)`);
        setProperties(response);
        setFilteredProperties(response);
        calculatePagination(response);
      } else {
        console.log('ManagePropertiesTab: No properties in response or invalid format');
        setProperties([]);
        setFilteredProperties([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('ManagePropertiesTab: Error fetching properties:', error);
      setErrorMessage('Could not fetch properties. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const calculatePagination = (data) => {
    const total = Math.ceil(data.length / propertiesPerPage);
    setTotalPages(total || 1); // Ensure minimum of 1 page
    setCurrentPage(1); // Reset to first page when data changes
  };

  const filterProperties = () => {
    let filtered = [...properties];
    
    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(property => 
        (property.address || '').toLowerCase().includes(search) ||
        (property.neighborhood || '').toLowerCase().includes(search) ||
        (property._id || property.id || '').toString().includes(search)
      );
    }
    
    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(property => property.propertyStatus === statusFilter);
    }
    
    // Apply type filter
    if (typeFilter) {
      filtered = filtered.filter(property => property.propertyType === typeFilter);
    }
    
    setFilteredProperties(filtered);
    calculatePagination(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getCurrentPageProperties = () => {
    const startIndex = (currentPage - 1) * propertiesPerPage;
    const endIndex = startIndex + propertiesPerPage;
    return filteredProperties.slice(startIndex, endIndex);
  };

  const confirmDelete = (property) => {
    setPropertyToDelete(property);
    setShowDeleteConfirm(true);
  };

  const cancelDelete = () => {
    setPropertyToDelete(null);
    setShowDeleteConfirm(false);
  };

  const executeDelete = async () => {
    if (!propertyToDelete) return;
    
    setIsLoading(true);
    
    try {
      await propertyService.deleteProperty(propertyToDelete._id || propertyToDelete.id);
      
      // Remove from local state
      const updatedProperties = properties.filter(p => 
        (p._id || p.id) !== (propertyToDelete._id || propertyToDelete.id)
      );
      
      setProperties(updatedProperties);
      
      setSuccessMessage(`Successfully deleted property: ${propertyToDelete.address}`);
      
      // Notify parent component of change
      if (onPropertyChanged) {
        onPropertyChanged();
      }
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error deleting property:', error);
      setErrorMessage(`Failed to delete property: ${error.message}`);
    } finally {
      setPropertyToDelete(null);
      setShowDeleteConfirm(false);
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 1) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}m ago`;
    return `${Math.floor(diffDays / 365)}y ago`;
  };

  // Get background class for property status
  const getStatusClass = (status) => {
    switch(status) {
      case 'For Sale': return 'badge badge-success';
      case 'Pending': return 'badge badge-warning';
      case 'Sold': return 'badge badge-danger';
      case 'Off Market': return 'badge badge-secondary';
      default: return 'badge';
    }
  };

  return (
    <div style={{ color: '#ffffff' }}>
      <style>{`
        /* Dark mode overrides for Property List */
        .section-title {
          color: #ffffff !important;
          margin-bottom: 24px;
        }
        .filter-container {
          background-color: rgba(26, 26, 26, 0.6) !important;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
          border-radius: 12px;
          padding: 24px !important;
          margin-bottom: 24px;
        }
        .grid-3-cols {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }
        .form-label {
          color: rgba(255, 255, 255, 0.7) !important;
        }
        .form-control {
          background-color: rgba(255, 255, 255, 0.05) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          color: #ffffff !important;
        }
        .form-control:focus {
          border-color: rgba(255, 255, 255, 0.4) !important;
          background-color: rgba(255, 255, 255, 0.08) !important;
        }
        .form-control option {
          background-color: #1a1a1a;
          color: #ffffff;
        }
        .alert-success {
          background-color: rgba(4, 120, 87, 0.15) !important;
          color: #4ade80 !important;
          border: 1px solid rgba(4, 120, 87, 0.3) !important;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        .alert-danger {
          background-color: rgba(220, 38, 38, 0.15) !important;
          color: #fca5a5 !important;
          border: 1px solid rgba(220, 38, 38, 0.3) !important;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        .table-responsive {
          background-color: rgba(26, 26, 26, 0.6) !important;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          overflow-x: auto;
        }
        .data-table {
          color: #ffffff !important;
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          background-color: transparent !important;
        }
        .data-table * {
          background-color: transparent !important;
        }
        .data-table thead {
          background-color: transparent !important;
        }
        .data-table th {
          color: rgba(255, 255, 255, 0.7) !important;
          border-bottom: 2px solid rgba(255, 255, 255, 0.1) !important;
          background-color: transparent !important;
          padding: 16px !important;
          font-weight: 600;
          text-transform: uppercase;
          font-size: 0.75rem;
          letter-spacing: 0.05em;
          text-align: left;
        }
        .data-table td {
          padding: 16px !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
          color: #ffffff !important;
          background-color: transparent !important;
        }
        .data-table tr {
          transition: background-color 0.2s;
          background-color: transparent !important;
        }
        .data-table tbody tr:hover {
          background-color: rgba(255, 255, 255, 0.03) !important;
        }
        .data-table tbody tr:hover * {
          background-color: transparent !important;
        }
        .property-cell {
          min-width: 250px;
        }
        .property-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .property-image {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          overflow: hidden;
          flex-shrink: 0;
        }
        .property-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .property-details {
          flex: 1;
          min-width: 0;
        }
        .property-address {
          font-weight: 600;
          color: #ffffff;
          margin-bottom: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .property-neighborhood {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.6);
        }
        .property-sqft {
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.875rem;
        }
        .badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }
        .badge-success {
          background-color: rgba(34, 197, 94, 0.2);
          color: #4ade80;
        }
        .badge-warning {
          background-color: rgba(251, 146, 60, 0.2);
          color: #fb923c;
        }
        .badge-danger {
          background-color: rgba(239, 68, 68, 0.2);
          color: #f87171;
        }
        .badge-secondary {
          background-color: rgba(148, 163, 184, 0.2);
          color: #94a3b8;
        }
        .btn-link {
          background: none;
          border: none;
          padding: 0;
          font-weight: 500;
          cursor: pointer;
          text-decoration: none;
          transition: opacity 0.2s;
        }
        .btn-link:hover {
          opacity: 0.7;
        }
        .text-blue {
          color: #60a5fa;
        }
        .text-red {
          color: #f87171;
        }
        .text-right {
          text-align: right;
        }
        .mr-2 {
          margin-right: 0.5rem;
        }
        .pagination-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }
        .pagination-info {
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.875rem;
        }
        .pagination-controls {
          display: flex;
          gap: 8px;
        }
        .pagination-arrow,
        .pagination-number {
          padding: 8px 12px;
          background-color: transparent;
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #ffffff;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.875rem;
        }
        .pagination-arrow:hover:not(:disabled),
        .pagination-number:hover {
          background-color: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.4);
        }
        .pagination-arrow:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
        .pagination-number.active {
          background-color: #4f46e5;
          border-color: #4f46e5;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal-dialog {
          background-color: rgba(26, 26, 26, 0.95);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          max-width: 500px;
          width: 90%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        }
        .modal-header {
          padding: 24px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .modal-header h3 {
          margin: 0;
          color: #ffffff;
          font-size: 1.25rem;
          font-weight: 600;
        }
        .modal-close {
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.6);
          font-size: 1.5rem;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          transition: all 0.2s;
        }
        .modal-close:hover {
          background-color: rgba(255, 255, 255, 0.1);
          color: #ffffff;
        }
        .modal-body {
          padding: 24px;
          color: rgba(255, 255, 255, 0.8);
        }
        .modal-footer {
          padding: 24px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }
        .btn {
          padding: 10px 20px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.875rem;
          border: none;
        }
        .btn-secondary {
          background-color: transparent;
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        .btn-secondary:hover {
          background-color: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.4);
        }
        .btn-danger {
          background-color: #ef4444;
          color: #ffffff;
        }
        .btn-danger:hover {
          background-color: #dc2626;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .loading-spinner {
          border: 3px solid rgba(255, 255, 255, 0.1);
          border-top-color: rgba(255, 255, 255, 0.8);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .text-center {
          text-align: center;
        }
        .p-6 {
          padding: 3rem;
        }
        .mt-3 {
          margin-top: 0.75rem;
        }
        .mb-4 {
          margin-bottom: 1rem;
        }
        .ml-2 {
          margin-left: 0.5rem;
        }
        .font-bold {
          font-weight: 700;
        }
        .text-sm {
          font-size: 0.875rem;
        }
        .font-medium {
          font-weight: 500;
        }
        .flex {
          display: flex;
        }
        @media (max-width: 768px) {
          .grid-3-cols {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      
      {successMessage && (
        <div className="alert-success">
          {successMessage}
        </div>
      )}
      
      {errorMessage && (
        <div className="alert-danger">
          {errorMessage}
        </div>
      )}
      
      {/* Search and Filter */}
      <div className="filter-container">
        <div className="grid-3-cols">
          <div className="form-group">
            <label className="form-label">Search</label>
            <input
              type="text"
              placeholder="Address, neighborhood, ID..."
              className="form-control"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Status</label>
            <select 
              className="form-control"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <option value="">All Statuses</option>
              <option value="For Sale">For Sale</option>
              <option value="Pending">Pending</option>
              <option value="Sold">Sold</option>
              <option value="Off Market">Off Market</option>
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label">Property Type</label>
            <select 
              className="form-control"
              value={typeFilter}
              onChange={handleTypeFilterChange}
            >
              <option value="">All Types</option>
              <option value="House">House</option>
              <option value="Condo">Condo</option>
              <option value="Apartment">Apartment</option>
              <option value="Townhouse">Townhouse</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Properties Table */}
      {isLoading ? (
        <div className="text-center p-6">
          <div className="loading-spinner"></div>
          <p className="mt-3">Loading properties...</p>
        </div>
      ) : filteredProperties.length === 0 ? (
        <div className="table-responsive">
          <div className="text-center p-6">
            <p style={{ color: 'rgba(255, 255, 255, 0.6)' }}>No properties found. Try adjusting your filters.</p>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Price</th>
                <th>Details</th>
                <th>Status</th>
                <th>Added</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getCurrentPageProperties().map((property, index) => (
                <tr key={property._id || property.id || index}>
                  <td className="property-cell">
                    <div className="property-info">
                      <div className="property-image">
                        <img 
                          src={property.mainImage || 'https://via.placeholder.com/60?text=No+Image'} 
                          alt={property.address}
                        />
                      </div>
                      <div className="property-details">
                        <div className="property-address">{property.address}</div>
                        <div className="property-neighborhood">
                          {typeof property.neighborhood === 'string' 
                            ? property.neighborhood 
                            : property.neighborhood?.name || 'Unknown'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{property.price}</td>
                  <td>
                    <div className="text-sm">
                      <span className="font-medium">{property.bedrooms} bd</span>
                      {' | '}
                      <span className="font-medium">{property.bathrooms} ba</span>
                      {' | '}
                      <span>{property.sqft?.toLocaleString() || 0} sqft</span>
                    </div>
                    <div className="property-sqft">
                      {property.propertyType} • {property.listingType === 'purchase' ? 'For Sale' : 'For Rent'}
                    </div>
                  </td>
                  <td>
                    <span className={getStatusClass(property.propertyStatus)}>
                      {property.propertyStatus || 'Unknown'}
                    </span>
                  </td>
                  <td>{formatDate(property.createdAt)}</td>
                  <td className="text-right">
                    <button 
                      className="btn-link text-blue mr-2"
                      onClick={() => window.open(`/admin/properties/edit/${property._id || property.id}`, '_blank')}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn-link text-red"
                      onClick={() => confirmDelete(property)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Pagination */}
          <div className="pagination-container">
            <div className="pagination-info">
              Showing {((currentPage - 1) * propertiesPerPage) + 1} to{' '}
              {Math.min(currentPage * propertiesPerPage, filteredProperties.length)} of{' '}
              {filteredProperties.length} properties
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-arrow"
              >
                &laquo; Prev
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`pagination-number ${currentPage === pageNum ? 'active' : ''}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-arrow"
              >
                Next &raquo;
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && propertyToDelete && (
        <div className="modal-overlay">
          <div className="modal-dialog">
            <div className="modal-header">
              <h3>Delete Property</h3>
              <button 
                className="modal-close"
                onClick={cancelDelete}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this property? This action cannot be undone.</p>
              <p className="mb-4 font-bold">{propertyToDelete.address}</p>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={cancelDelete}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger ml-2"
                onClick={executeDelete}
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagePropertiesTab;